import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../config/api';

const SESSION_KEY = 'godash.analytics.sessionId';
const QUEUE_FLUSH_MS = 800;
const MAX_BATCH = 20;

let eventQueue = [];
let flushTimer = null;
let flushInFlight = false;

function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return `s_${Date.now().toString(36)}`;
  }
}

async function flushQueue() {
  if (flushInFlight || eventQueue.length === 0) return;
  const batch = eventQueue.splice(0, MAX_BATCH);
  flushInFlight = true;
  try {
    await api.post('/product-analytics/events', { events: batch });
  } catch {
    // Best-effort telemetry — do not block UI or retry forever
  } finally {
    flushInFlight = false;
    if (eventQueue.length > 0) scheduleFlush();
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushQueue();
  }, QUEUE_FLUSH_MS);
}

/**
 * Enqueue a product analytics event (batched). Safe to call from anywhere.
 */
export function trackProductEvent({ event_type = 'feature_action', event_name, feature_key, path, properties } = {}) {
  if (!localStorage.getItem('token')) return;
  if (!event_name && !feature_key && !path) return;

  eventQueue.push({
    event_type,
    event_name: event_name || feature_key || `page.${path || '/'}`,
    feature_key: feature_key || null,
    path: path || null,
    properties: properties || null,
    session_id: getSessionId(),
  });

  if (eventQueue.length >= MAX_BATCH) {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    flushQueue();
  } else {
    scheduleFlush();
  }
}

export function trackFeature(featureKey, eventName, properties) {
  trackProductEvent({
    event_type: 'feature_action',
    feature_key: featureKey,
    event_name: eventName || featureKey,
    properties,
  });
}

export function trackPageView(path) {
  trackProductEvent({
    event_type: 'page_view',
    path,
    event_name: `page.${String(path || '/').replace(/\//g, '.').replace(/^\.+|\.+$/g, '') || 'home'}`,
  });
}

/**
 * Automatic page-view tracking for authenticated app shell.
 */
export function useProductAnalyticsTracking() {
  const location = useLocation();
  const lastPath = useRef('');

  useEffect(() => {
    const path = `${location.pathname}${location.search || ''}`;
    if (path === lastPath.current) return;
    lastPath.current = path;
    trackPageView(location.pathname);
  }, [location.pathname, location.search]);
}
